import {useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {useInView} from 'react-intersection-observer';
import useEmblaCarousel from 'embla-carousel-react';

import type {SanityModulePopupGrid} from '../../types';

import ModuleWrapper from './ModuleWrapper.client';
import Popup from '../ui/Popup.client';

import {isMobile} from '../ui/animationVars';
import {gridVars} from '../ui/animationVars';
import {contentVars} from '../ui/animationVars';
import {Image} from '@shopify/hydrogen';

import slugify from 'slugify';

const MobileGrid = ({items, setIsOpen}: any) => {
  /* Carousel & button handling */
  const [viewportRef] = useEmblaCarousel({
    align: 'start',
    dragFree: true,
    loop: false,
    containScroll: 'keepSnaps',
  });

  return (
    <div ref={viewportRef} className="w-full">
      <div className="flex gap-x-6 pt-space-s lg:hidden">
        {items?.map(({image, title, excerpt}: any, i: number) => (
          <motion.div
            variants={contentVars}
            key={i}
            onClick={() => setIsOpen(i)}
            style={{flex: '0 0 auto'}}
            className="relative z-10 w-[85%] cursor-pointer overflow-hidden rounded-md"
          >
            <div className="aspect-w-4 aspect-h-3 relative w-full">
              <Image
                width="inherit"
                height="inherit"
                data={image}
                alt={title}
                className="transition-standard absolute top-0 left-0 h-full w-full object-cover hover:scale-105"
              />
              <div className="pointer-events-none absolute top-0 left-0 h-full w-full bg-black bg-opacity-50 object-cover" />
              <div className="pointer-events-none flex h-full w-full items-end justify-between gap-x-4 p-6">
                <div>
                  <h4 className="richH4 font-bold text-orange">{title}</h4>
                  <p className="richH7 mt-1 font-bold text-white">{excerpt}</p>
                </div>
                <div className="w-max">
                  <div className="mb-1 flex h-7 w-7 items-center justify-center rounded-full bg-orange bg-opacity-30">
                    <svg
                      width="16"
                      height="15"
                      viewBox="0 0 16 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8.03905 0.398682C7.61271 0.398682 7.26715 0.744243 7.26715 1.17059V6.76739H1.67034C1.244 6.76739 0.898438 7.11295 0.898438 7.5393C0.898438 7.96564 1.244 8.3112 1.67034 8.3112H7.26715V13.908C7.26715 14.3343 7.61271 14.6799 8.03905 14.6799C8.46539 14.6799 8.81096 14.3343 8.81096 13.908V8.3112H14.4078C14.8341 8.3112 15.1797 7.96564 15.1797 7.5393C15.1797 7.11295 14.8341 6.76739 14.4078 6.76739H8.81096V1.17059C8.81096 0.744243 8.46539 0.398682 8.03905 0.398682V0.398682Z"
                        fill="#F27021"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const DesktopGrid = ({items, setIsOpen}: any) => {
  return (
    <div className="hidden grid-cols-1 gap-6 pt-space-s md:grid-cols-2 md:gap-10 lg:grid lg:grid-cols-3">
      {items?.map(({image, title, excerpt}: any, i: number) => (
        <motion.div
          variants={contentVars}
          key={i}
          onClick={() => setIsOpen(i)}
          className="relative z-10 h-full w-full cursor-pointer overflow-hidden rounded-md"
        >
          <div className="aspect-w-4 aspect-h-3 relative w-full">
            <Image
              width="inherit"
              height="inherit"
              data={image}
              alt={title}
              className="transition-standard absolute top-0 left-0 h-full w-full object-cover hover:scale-105"
            />
            <div className="pointer-events-none absolute top-0 left-0 h-full w-full bg-black bg-opacity-50 object-cover" />
            <div className="pointer-events-none flex h-full w-full items-end justify-between gap-x-4 p-6">
              <div>
                <h4 className="richH4 font-bold text-orange">{title}</h4>
                <p className="richH7 mt-1 font-bold text-white">{excerpt}</p>
              </div>
              <div className="w-max">
                <div className="mb-1 flex h-7 w-7 items-center justify-center rounded-full bg-orange bg-opacity-30">
                  <svg
                    width="16"
                    height="15"
                    viewBox="0 0 16 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.03905 0.398682C7.61271 0.398682 7.26715 0.744243 7.26715 1.17059V6.76739H1.67034C1.244 6.76739 0.898438 7.11295 0.898438 7.5393C0.898438 7.96564 1.244 8.3112 1.67034 8.3112H7.26715V13.908C7.26715 14.3343 7.61271 14.6799 8.03905 14.6799C8.46539 14.6799 8.81096 14.3343 8.81096 13.908V8.3112H14.4078C14.8341 8.3112 15.1797 7.96564 15.1797 7.5393C15.1797 7.11295 14.8341 6.76739 14.4078 6.76739H8.81096V1.17059C8.81096 0.744243 8.46539 0.398682 8.03905 0.398682V0.398682Z"
                      fill="#F27021"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default function PopupGridModule({
  module,
}: {
  module: SanityModulePopupGrid;
}) {
  const {config, heading, items}: any = module;
  const [isOpen, setIsOpen] = useState<any>(false);

  const {ref, inView} = useInView({
    threshold: 0.7,
    triggerOnce: true,
  });

  return (
    <>
      {/* Grid */}
      <ModuleWrapper {...config} className="overflow-hidden px-gutter">
        <motion.div
          ref={ref}
          variants={gridVars}
          animate={!isMobile && (inView ? 'animate' : 'initial')}
        >
          <motion.h6
            variants={contentVars}
            className="blockH6 font-bold"
            data-testid={`section-${slugify(heading, {
              lower: true,
              trim: true,
            })}`}
          >
            {heading}
          </motion.h6>
          <DesktopGrid items={items} setIsOpen={setIsOpen} />
          <MobileGrid items={items} setIsOpen={setIsOpen} />
        </motion.div>
      </ModuleWrapper>
      {/* Popup */}
      <AnimatePresence mode="wait">
        {isOpen !== false && (
          <Popup key={isOpen} setIsOpen={setIsOpen} {...items[isOpen]} />
        )}
      </AnimatePresence>
    </>
  );
}
